import { Injectable } from '@angular/core';
// Service
import { BroadcastService } from '@cv-invision/core';
import { CVfUtilsService } from '../../../../../services/cvf-utils.service';
import { ExternalApiService } from '../../../../../services/external-api.service';
import { ViewsService } from '../../../../../../services/views.service';
// Model
import { ResourceModel, Snapshot } from '../model/resource-media.model';
import { ReportImageTargetPath } from '../model/report-image.model';
import { SmartTagFormModel } from '../model/smart-tag-form.model';
import { SmartTagErrorModel, SmartTagModel } from '../../smart-tags/model/smartTag.model';
import { SmartTagTemplate } from '../../smart-tags/model/smart-tag-template.model';
import { SmartTagTemplateModelClass } from '../../smart-tags/model';
// Other
import { Subject } from 'rxjs';

@Injectable()
export class ResourceMediaControlService {
	resourceModelChange: Subject<ResourceModel> = new Subject<ResourceModel>();

	constructor(
		private broadcastService: BroadcastService,
		private cvfUtils: CVfUtilsService,
		private externalAPIService: ExternalApiService,
		private viewsService: ViewsService
	) {
	}

	/**
	 * Create a new Report Image
	 */
	public async createReportImage(
		roomData: any,
		targetJSONPath: string,
		selectedContiguousRoom: any,
		snapshot: Snapshot,
	): Promise<ReportImageTargetPath> {

		// Trick the JS
		const res: Response = await fetch(snapshot.blob);
		const blob: Blob = await res.blob();

		let title = roomData.cv_Label;
		let jsonPath = targetJSONPath;
		if (jsonPath !== selectedContiguousRoom.key) {
			title = selectedContiguousRoom.label;
			jsonPath = selectedContiguousRoom.key;
		}

		const reportImageTargetPath: ReportImageTargetPath = new ReportImageTargetPath(jsonPath);

		const postData = {
			name: title,
			jsonPath: jsonPath,
			fileData: blob,
			latitude: snapshot.metadata.latitude,
			longitude: snapshot.metadata.longitude
		};

		let createError =  new SmartTagErrorModel();
		const response = await this.externalAPIService.createAttachment(this.viewsService.order.id, postData)
			.toPromise()
			.catch(e => createError = e.error);

		if (response !== null && response.id) {
			const attachment = await this.externalAPIService.getAttachmentById(this.viewsService.order.id, response.id).toPromise();
			this.addNewAttachmentToService(attachment);
		}

		// All  good
		if (response && response.id) {
			return reportImageTargetPath;
		}

		// Oopsie, an error has occured
		if (createError.succeed !== null) {
			return {
				jsonPath: null,
				error: createError
			}
		}

		return null;
	}

	/**
	 * Create a new Smart Tag Image
	 */
	public async createSmartTag(
		roomData: any,
		smartTagFormModel: SmartTagFormModel,
		targetJSONPath: string,
		snapshot: Snapshot,
	): Promise<SmartTagModel> {

		let blob: Blob = snapshot.blob;
		// If the Blob is not requested, trick JS to get one
		if (!snapshot.requestedBlob) {
			// Trick the JS
			const res: Response = await fetch(snapshot.blob);
			blob = await res.blob();
		}
		// Create the Smart Tag Data
		const newSmartTagData: any = Object.assign(
			{
				isOutsideArea: roomData.isOutsideArea,
				isVisible: true
			},
			smartTagFormModel.smartTagData,
			{
				tagType: smartTagFormModel.selectedSmartTagItem.smartTagType.key,
				subCategory: smartTagFormModel.selectedSmartTagItem.subCategory.value,
				title: smartTagFormModel.selectedSmartTagItem.subCategory.value
			},
			{
				imageless: smartTagFormModel.imageless
			}
		);

		const jsonPath = (
			smartTagFormModel.selectedSmartTagItem.rootParent &&
			smartTagFormModel.selectedSmartTagItem.rootParent.pathTo
		) ||
			smartTagFormModel.selectedSmartTagItem.smartTagType.pathTo;

		let postTargetJSONPath = `${targetJSONPath}.smartTags`;
		if (!smartTagFormModel.asGroupKey) {
			postTargetJSONPath = postTargetJSONPath + `.${jsonPath}`
		} if (smartTagFormModel.asGroupKey) {
			newSmartTagData.groupName = jsonPath;
			newSmartTagData.tagType = smartTagFormModel.selectedSmartTagItem.subCategory.key;
		}

		// Prepare the Post Data for the Server
		const postData = {
			name: roomData.cv_Label,
			jsonPath: postTargetJSONPath,
			fileData: blob,
			latitude: snapshot.metadata.latitude,
			longitude: snapshot.metadata.longitude,
			smartTagData: newSmartTagData
		};

		let createError =  new SmartTagErrorModel();

		const response: any = await this.externalAPIService.createSmartTagWithAttachment(this.viewsService.order.id, postData)
			.toPromise()
			.catch(e => createError = e.error);

		if (response && response.id) {
			const attachment = await this.externalAPIService.getAttachmentById(this.viewsService.order.id, response.id).toPromise();
			// Add new Attachment
			this.addNewAttachmentToService(attachment);
			newSmartTagData.smartTagObjectId = response.smartTagId;
			newSmartTagData.hasPhotos = true;
			newSmartTagData._Path = attachment.refJsonPath;
		}

		// All  good
		if (response !== null && response.id !== null) {
			return newSmartTagData;
		}

		// Oopsie, an error has occured
		if (createError.succeed !== null) {
			return {
				error: createError
			}
		}

		return null;
	}

	/**
	 *
	 * @param orderId
	 * @param orderDesktopModel
	 */
	public async patchSmartTag(resourceModel: ResourceModel): Promise<any> {

		const newSmartTagData: SmartTagModel = resourceModel.smartTagForm.smartTagData;

        const smartTagData: any = [];
        // Select the Tag Template to Evaluate
        const tagTemplate = this.cvfUtils.getSelectedViewMode(resourceModel.smartTagForm.selectedSmartTagItem);
        if (tagTemplate !== null && tagTemplate !== SmartTagTemplate.DEFAULT) {
            // we do this to remove properties that do not belong to the Template
            const renderer = new SmartTagTemplateModelClass[tagTemplate];

            if (tagTemplate === SmartTagTemplate.RENDER_FORM_RENDER) {
                const formControls = this.cvfUtils.getSelectedFormControls(resourceModel.smartTagForm.selectedSmartTagItem);
                for (const formControl of formControls) {
                    renderer[formControl.field] = '';
                }
            }

            for (const [key, value] of Object.entries(renderer)) {
                if (newSmartTagData[key] || newSmartTagData[key] === null) {
                    smartTagData.push({
                        path: `${newSmartTagData._Path}.${key}`,
                        value: newSmartTagData[key]
                    });
                } else if (newSmartTagData[key] === false || newSmartTagData[key] === '') {
                    smartTagData.push({
                        path: `${newSmartTagData._Path}.${key}`,
                        value: null
                    });
                }
            }

            // On editing if we change the template, nullify all properties and change the bucket
            const originalSmartTagTemplate = resourceModel.smartTagForm.smartTagTemplateChangeFrom;
            if (originalSmartTagTemplate && originalSmartTagTemplate !== null && originalSmartTagTemplate !== SmartTagTemplate.DEFAULT) {
                const tagTemplate = this.cvfUtils.getSelectedViewMode(resourceModel.smartTagForm.selectedSmartTagItem);
                if (originalSmartTagTemplate !== tagTemplate) {
                    const renderer = new SmartTagTemplateModelClass[originalSmartTagTemplate];

                    // if (originalSmartTagTemplate === SmartTagTemplate.RENDER_FORM_RENDER) {
                    //     const formControls = resourceModel.smartTagForm.smartTagTemplateChangeFromFields;
                    //     for (const formControl of formControls) {
                    //         renderer[formControl.field] = '';
                    //     }
                    // }

                    for (const [key, value] of Object.entries(renderer)) {
                        newSmartTagData[key] = null;
                        smartTagData.push({
                            path: `${newSmartTagData._Path}.${key}`,
                            value: null
                        });
                    }
                }
            }
        }

		// Re-send all possible values for the Type
		if (resourceModel.smartTagForm.selectedSmartTagItem !== null) {
			const groupName = (
				resourceModel.smartTagForm.selectedSmartTagItem.rootParent &&
				resourceModel.smartTagForm.selectedSmartTagItem.rootParent.pathTo
			) ||
				resourceModel.smartTagForm.selectedSmartTagItem.smartTagType.pathTo;
			// Set New Data if the Model changed
			newSmartTagData.groupName = groupName;
			newSmartTagData.tagType = resourceModel.smartTagForm.selectedSmartTagItem.subCategory.key;
			newSmartTagData.subCategory = resourceModel.smartTagForm.selectedSmartTagItem.subCategory.value;
			newSmartTagData.title = resourceModel.smartTagForm.selectedSmartTagItem.subCategory.value;

			// Set All params
			smartTagData.push(...[{
				path: `${resourceModel.smartTagForm.smartTagData._Path}.groupName`,
				value: newSmartTagData.groupName
			}, {
				path: `${resourceModel.smartTagForm.smartTagData._Path}.tagType`,
				value: newSmartTagData.tagType
			}, {
				path: `${resourceModel.smartTagForm.smartTagData._Path}.subCategory`,
				value: newSmartTagData.subCategory
			}, {
				path: `${resourceModel.smartTagForm.smartTagData._Path}.title`,
				value: newSmartTagData.title
			}]);
		}

		const description = resourceModel.smartTagForm.smartTagData['description'];

		// Always include the Description
		smartTagData.push({
			path: `${resourceModel.smartTagForm.smartTagData._Path}.description`,
			value: (description) ? description : ''
		});

		const response: any = await this.externalAPIService.patchDocument(this.viewsService.order.id, smartTagData, this.viewsService.etag).toPromise();

		// All  good
		if (response && response !== null) {
			return newSmartTagData;
		}

		return null;
	}

	/**
	 * Add new Elements to the UI and notify to anyone listening to it
	 * @private
	 */
	private addNewAttachmentToService(attachment: any,): void {
		this.viewsService.photos.push(attachment);
		if (!(attachment.refJsonPath in this.viewsService.flattenedPhotos)) {
			const nObj = [];
			nObj[attachment.refJsonPath] = [];

			Object.assign(this.viewsService.flattenedPhotos, nObj);
		}
		const exist = this.viewsService.flattenedPhotos[attachment.refJsonPath].filter(item => item.id === attachment.id) || [];

		if (exist.length === 0) {
			this.viewsService.flattenedPhotos[attachment.refJsonPath].push(attachment);
		}
	}
}
